import React from 'react';
import { Button, DatePicker, Input, Select, Space, Tooltip } from 'antd';
import { ArrowDownOutlined, ArrowUpOutlined, LockOutlined } from '@ant-design/icons';
import { DataTable } from '../../ui';
import dayjs from 'dayjs';
import type { DeliveryGroupStopDraft, DeliveryGroupStopType } from './deliveryGroupTypes';
import { canDeleteStop, isStopExecutionLocked, stopMutationWarning } from './executionSafeStop';

const { Option } = Select;

export type StopTableContext = {
  warehouses?: Array<{ id: number; name: string; city?: string; address?: string; contactPerson?: string; phone?: string }>;
  applications?: Array<{
    id: number;
    internalNumber?: string;
    loadingAddress?: string;
    unloadingAddress?: string;
    loadingContactPerson?: string;
    loadingPhone?: string;
    unloadingContactPerson?: string;
    unloadingPhone?: string;
    client?: { name?: string };
    consignee?: { name?: string; contactPerson?: string; phone?: string };
  }>;
  consignees?: Array<{ id: number; name: string; address?: string; contactPerson?: string; phone?: string; city?: string }>;
  /** Loading: all apps; unloading: group-scoped apps */
  applicationOptions: Array<{ id: number; label: string }>;
  resolveCityHint?: (address: string) => Promise<string | null>;
  normalizeCity?: (city: string | null | undefined) => string | null;
  inTransit?: boolean;
};

type Props = {
  stopType: DeliveryGroupStopType;
  stops: DeliveryGroupStopDraft[];
  onChange: (next: DeliveryGroupStopDraft[]) => void;
  ctx: StopTableContext;
  makeStopUid: () => string;
  groupUid: string;
};

export function DeliveryGroupStopTable({
  stopType,
  stops,
  onChange,
  ctx,
  makeStopUid,
  groupUid
}: Props) {
  const isLoading = stopType === 'loading';

  const updateStop = (uid: string, patch: Partial<DeliveryGroupStopDraft>) => {
    onChange(stops.map((s) => (s.uid === uid ? { ...s, ...patch } : s)));
  };

  const removeStop = (uid: string) => {
    onChange(stops.filter((s) => s.uid !== uid));
  };

  const moveStop = (uid: string, dir: -1 | 1) => {
    const idx = stops.findIndex((s) => s.uid === uid);
    if (idx < 0) return;
    const nextIdx = idx + dir;
    if (nextIdx < 0 || nextIdx >= stops.length) return;
    const copy = [...stops];
    [copy[idx], copy[nextIdx]] = [copy[nextIdx], copy[idx]];
    onChange(copy);
  };

  const addStop = () => {
    onChange([
      ...stops,
      {
        uid: makeStopUid(),
        groupUid,
        type: stopType,
        pointType: null,
        pointId: null,
        address: null,
        city: null,
        contactPerson: null,
        phone: null,
        plannedAt: null,
        actualAt: null,
        notes: null,
        status: null
      }
    ]);
  };

  const fillFromPoint = async (stop: DeliveryGroupStopDraft, pointId: number) => {
    const pt = String(stop.pointType || '').toLowerCase();
    const patch: Partial<DeliveryGroupStopDraft> = { pointId };
    if (pt === 'warehouse') {
      const w = ctx.warehouses?.find((x) => x.id === pointId);
      if (w) {
        patch.address = w.address || `${w.name}, ${w.city || ''}`;
        patch.city = ctx.normalizeCity?.(w.city) ?? w.city ?? null;
        patch.contactPerson = w.contactPerson || null;
        patch.phone = w.phone || null;
      }
    } else if (pt === 'application') {
      const a = ctx.applications?.find((x) => x.id === pointId);
      if (a) {
        const address = isLoading ? a.loadingAddress : a.unloadingAddress;
        patch.address = address || null;
        if (address && ctx.resolveCityHint) {
          const hint = await ctx.resolveCityHint(address);
          patch.city = ctx.normalizeCity?.(hint) ?? hint ?? null;
        }
        patch.contactPerson = isLoading
          ? a.loadingContactPerson || null
          : a.unloadingContactPerson || a.consignee?.contactPerson || null;
        patch.phone = isLoading ? a.loadingPhone || null : a.unloadingPhone || a.consignee?.phone || null;
      }
    } else if (pt === 'consignee') {
      const c = ctx.consignees?.find((x) => x.id === pointId);
      if (c) {
        patch.address = c.address || null;
        patch.city = ctx.normalizeCity?.((c as { city?: string }).city) ?? null;
        patch.contactPerson = c.contactPerson || null;
        patch.phone = c.phone || null;
      }
    }
    updateStop(stop.uid, patch);
  };

  const columns = [
    {
      title: '#',
      width: 40,
      render: (_: unknown, __: DeliveryGroupStopDraft, index: number) => index + 1
    },
    {
      title: 'Тип',
      width: 130,
      render: (_: unknown, record: DeliveryGroupStopDraft) => (
        <Select
          style={{ width: '100%' }}
          value={record.pointType || undefined}
          placeholder="Тип"
          disabled={isStopExecutionLocked(record)}
          onChange={(val) => updateStop(record.uid, { pointType: val as any, pointId: null })}
          options={
            isLoading
              ? [
                  { value: 'warehouse', label: 'Склад' },
                  { value: 'application', label: 'Заявка' }
                ]
              : [
                  { value: 'warehouse', label: 'Склад' },
                  { value: 'application', label: 'Заявка доставки' },
                  { value: 'consignee', label: 'Получатель' }
                ]
          }
        />
      )
    },
    {
      title: isLoading ? 'Заявка / склад' : 'Заявка / склад / получатель',
      width: 220,
      render: (_: unknown, record: DeliveryGroupStopDraft) => (
        <Select
          style={{ width: '100%' }}
          showSearch
          optionFilterProp="children"
          placeholder="Выбор"
          value={record.pointId || undefined}
          disabled={!record.pointType || isStopExecutionLocked(record)}
          onChange={(val) => void fillFromPoint(record, Number(val))}
        >
          {record.pointType === 'warehouse' &&
            ctx.warehouses?.map((w) => (
              <Option key={w.id} value={w.id}>
                {w.name}
                {w.city ? ` — ${w.city}` : ''}
              </Option>
            ))}
          {record.pointType === 'application' &&
            (isLoading
              ? ctx.applications?.map((a) => (
                  <Option key={a.id} value={a.id}>
                    {a.internalNumber}
                    {a.client?.name ? ` — ${a.client.name}` : ''}
                  </Option>
                ))
              : ctx.applicationOptions.map((a) => (
                  <Option key={a.id} value={a.id}>
                    {a.label}
                  </Option>
                )))}
          {record.pointType === 'consignee' &&
            ctx.consignees?.map((c) => (
              <Option key={c.id} value={c.id}>
                {c.name}
              </Option>
            ))}
        </Select>
      )
    },
    {
      title: 'Адрес',
      render: (_: unknown, record: DeliveryGroupStopDraft) => (
        <Input
          value={record.address || ''}
          disabled={isStopExecutionLocked(record)}
          onChange={(e) => updateStop(record.uid, { address: e.target.value })}
        />
      )
    },
    {
      title: 'Контакт',
      width: 120,
      render: (_: unknown, record: DeliveryGroupStopDraft) => (
        <Input
          value={record.contactPerson || ''}
          disabled={isStopExecutionLocked(record)}
          onChange={(e) => updateStop(record.uid, { contactPerson: e.target.value })}
        />
      )
    },
    {
      title: 'Телефон',
      width: 110,
      render: (_: unknown, record: DeliveryGroupStopDraft) => (
        <Input
          value={record.phone || ''}
          disabled={isStopExecutionLocked(record)}
          onChange={(e) => updateStop(record.uid, { phone: e.target.value })}
        />
      )
    },
    {
      title: 'Дата',
      width: 130,
      render: (_: unknown, record: DeliveryGroupStopDraft) => (
        <DatePicker
          format="DD.MM.YYYY"
          style={{ width: '100%' }}
          disabled={isStopExecutionLocked(record)}
          value={record.plannedAt ? dayjs(record.plannedAt) : undefined}
          onChange={(d) => updateStop(record.uid, { plannedAt: d ? d.toISOString() : null })}
        />
      )
    },
    {
      title: '',
      width: 120,
      render: (_: unknown, record: DeliveryGroupStopDraft) => {
        const lockCtx = { inTransit: Boolean(ctx.inTransit) };
        const locked = isStopExecutionLocked(record, lockCtx, stops);
        const warn = stopMutationWarning(record, lockCtx, stops);
        const deletable = canDeleteStop(record, lockCtx, stops);
        return (
          <Space size={4}>
            {locked && (
              <Tooltip title={warn}>
                <LockOutlined style={{ color: '#faad14' }} />
              </Tooltip>
            )}
            <Button size="small" icon={<ArrowUpOutlined />} onClick={() => moveStop(record.uid, -1)} />
            <Button size="small" icon={<ArrowDownOutlined />} onClick={() => moveStop(record.uid, 1)} />
            <Button size="small" danger disabled={!deletable} onClick={() => removeStop(record.uid)}>
              ×
            </Button>
          </Space>
        );
      }
    }
  ];

  return (
    <div>
      <DataTable
        size="small"
        pagination={false}
        dataSource={stops.map((s) => ({ ...s, key: s.uid }))}
        columns={columns}
        locale={{ emptyText: isLoading ? 'Нет доп. точек погрузки' : 'Нет точек выгрузки' }}
      />
      <Button type="dashed" size="small" style={{ marginTop: 8 }} onClick={addStop}>
        + {isLoading ? 'Точка погрузки' : 'Точка выгрузки'}
      </Button>
    </div>
  );
}
